/**
 * AI 출력의 병원 가명 마커(⑆의료기관A(라벨))를 실제 병원명으로 복원하는 유틸(역변환 전용).
 *
 * 배경: 백엔드가 병원을 `⑆의료기관A(정형외과의원)` 형태로 가명 표기하고 AI가 이를 출력에 쓴다.
 * 라벨 생성(진료과·종별 파싱)은 백엔드 `hospital-context.util.ts` 단일 지점으로 이관됐고(로직 1벌 원칙),
 * 프론트 사본은 **복원 함수만** 남긴다(spec 2026-08-27 · 구현 노트).
 *
 * 계약(spec 계약면):
 *  - `demaskHospitalReferences(text, aliasMap, render)` — `render: 'link' | 'plain'`
 *  - alias 대문자 `[A-Z]{1,2}` (2글자 우선), 비교는 대문자 정규화 후
 *  - 매핑 부재 alias는 `⑆`만 벗긴 평문
 *  - 병원명 내 `[`·`]`는 전각 `［`·`］`로 치환(마크다운 링크 문법 파손 방지)
 *  - 괄호는 **라벨 어휘**에 매칭될 때만 소비(질환 나열 괄호는 보존)
 */

/** 복원용 alias→병원 매핑. label은 복원 텍스트엔 불필요하나 계약 타입 통일을 위해 optional로 받는다. */
export type HospitalAliasMap = Record<string, { name: string; label?: string }>;

/** 병원명으로 취급하지 않을 문자열(수기 입력의 결측 방어 — PrescriptionTableDisplay:343 방어와 동일). */
const UNKNOWN_HOSPITAL_NAMES = new Set(["null", "undefined", "-", ""]);

/**
 * 처방전 그룹에서 병원 alias→{name,label} 맵을 구성한다(3단·인쇄·팝업 공용).
 * alias는 대문자 정규화, 표시명(name)은 첫 등장 원문을 유지한다(계약: alias 키 정규화).
 */
export function buildHospitalAliasMapFromGroups(
  groups:
    | Array<{ hospital?: string; hospitalAlias?: string; hospitalLabel?: string }>
    | undefined
    | null
): HospitalAliasMap {
  const map: HospitalAliasMap = {};
  if (!groups) return map;
  for (const g of groups) {
    const alias = g.hospitalAlias ? String(g.hospitalAlias).toUpperCase() : "";
    const name = g.hospital ? String(g.hospital).trim() : "";
    if (!alias || UNKNOWN_HOSPITAL_NAMES.has(name)) continue;
    if (!map[alias]) map[alias] = { name, label: g.hospitalLabel };
  }
  return map;
}

/** 임베드 응답의 hospitalAliases(Record<alias,name>)를 복원 맵으로 변환(label 없음). */
export function buildHospitalAliasMapFromRecord(
  record: Record<string, string> | undefined | null
): HospitalAliasMap {
  const map: HospitalAliasMap = {};
  if (!record) return map;
  for (const [alias, name] of Object.entries(record)) {
    const key = String(alias).toUpperCase();
    const trimmed = name ? String(name).trim() : "";
    if (!key || UNKNOWN_HOSPITAL_NAMES.has(trimmed)) continue;
    if (!map[key]) map[key] = { name: trimmed };
  }
  return map;
}

/**
 * 라벨 어휘 폐쇄 목록(백엔드 라벨 SSOT의 프론트 사본 — 괄호 소비 판정 전용).
 * spec '라벨 문법' 표에서 파생: 진료과 canonical + 종별 표시명 + 도메인.
 * 라벨 생성에는 쓰지 않는다(백엔드 담당). 여기선 "이 괄호가 라벨인가?" 판정에만 쓴다.
 */
const LABEL_WORDS: string[] = [
  // 진료과 canonical
  "소아청소년과", "정신건강의학과", "마취통증의학과", "진단검사의학과", "직업환경의학과",
  "예방의학과", "방사선종양학과", "재활의학과", "이비인후과", "비뇨의학과", "가정의학과",
  "응급의학과", "영상의학과", "산부인과", "정형외과", "신경외과", "성형외과", "흉부외과",
  "핵의학과", "병리과", "결핵과", "피부과", "신경과", "안과", "내과", "외과",
  // 종별 표시명
  "상급종합병원", "종합병원", "정신병원", "요양병원", "한방병원", "치과의원", "치과병원",
  "상급종합", "보건기관", "한의원", "조산원", "치과", "의원", "병원", "약국",
  // 도메인(폴백 분리형)
  "근골격계", "여성의학",
];

// longest-match 우선을 위해 길이 내림차순 정렬 후 alternation 구성.
const LABEL_WORD_RE = new RegExp(
  LABEL_WORDS.slice().sort((a, b) => b.length - a.length).join("|"),
  "g"
);

/**
 * 괄호 내용이 라벨 어휘로만 이루어졌는지 판정한다.
 * 라벨 단어와 구분자(·, /)·공백을 모두 제거해 남는 문자가 없으면 라벨로 본다.
 * (질환 나열 `소화기질환·불면증`은 라벨 단어가 없어 잔여가 남으므로 라벨이 아니다 → 소비하지 않음)
 */
function isLabelParen(inner: string): boolean {
  const stripped = inner.replace(LABEL_WORD_RE, "").replace(/[·/\s]/g, "");
  return stripped.length === 0 && inner.trim().length > 0;
}

/** 마크다운 링크 텍스트 파손 방지: 실명 내 대괄호를 전각으로 치환. */
function escapeForLinkText(name: string): string {
  return name.replace(/\[/g, "［").replace(/\]/g, "］");
}

// (중괄호?) ⑆ [공백] (기관명사+공백)? (alias 1~2글자, 뒤가 영문/_면 ref 코드 → 제외)
//   (공백+기관명사)? (공백+괄호)? (중괄호?)
// 주의: 마커에 속하지 않는 뒤따르는 공백은 소비하지 않는다(뒷 기관명사/괄호가 실제로 있을 때만
//   그 앞 공백을 함께 소비) — 맨몸 `⑆A 재방문`이 `[..]재방문`으로 붙는 것 방지.
// 설계 결정 12(중괄호 관용 흡수): 모델이 근거코드 유추로 `{⑆의료기관A(라벨)}`을 쓸 위험이 있어,
//   토큰 바로 앞뒤의 균형 중괄호를 함께 소비해 잔해 없이 복원한다. 불균형/무괄호는 비파괴.
const NOUN = "(?:의료기관|의원|병원|기관)";
const HOSPITAL_RE = new RegExp(
  `(\\{)?⑆\\s*(?:${NOUN}\\s*)?([A-Za-z]{1,2})(?![A-Za-z_])(?:\\s*${NOUN})?(?:\\s*(\\([^)]*\\)))?(\\})?`,
  "g"
);

/**
 * AI 출력 텍스트의 ⑆ 병원 참조 마커를 실제 병원명으로 복원한다.
 *
 * 허용 변형: 어순(⑆의료기관A / ⑆A의료기관) · 단어 변형/누락(병원/의원/기관/없음) · 공백 · 라벨 유무.
 * ⑆상담필요·⑆근거확인필요·⑆img_·ref 코드(⑆CCMM_ 등)는 정규식 구조상 매칭되지 않아 보존된다.
 *
 * @param text AI 출력 텍스트
 * @param aliasToName alias(대문자 정규화) → { name, label? }
 * @param render 'link' → `[실명](#hospital-A)` 중간형 / 'plain' → 실명 평문
 */
export function demaskHospitalReferences(
  text: string | null | undefined,
  aliasToName: HospitalAliasMap,
  render: "link" | "plain"
): string {
  if (!text) return text ?? "";

  return text.replace(
    HOSPITAL_RE,
    (whole: string, lb?: string, key?: string, paren?: string, tb?: string) => {
      // 균형 중괄호일 때만 양쪽을 소비(잔해 없이). 불균형이면 있는 쪽을 재부착(비파괴).
      const balanced = !!lb && !!tb;
      const prefix = balanced ? "" : lb || "";
      const suffix = balanced ? "" : tb || "";

      const alias = String(key).toUpperCase();
      const entry = aliasToName[alias];

      // 매핑 부재: 파괴하지 않고 ⑆(+균형 중괄호)만 벗긴 평문으로 남긴다(계약).
      if (!entry) {
        let stripped = whole;
        if (balanced) stripped = stripped.replace(/^\{/, "").replace(/\}$/, "");
        return stripped.replace(/⑆/g, "");
      }

      // 괄호: 라벨 어휘면 소비, 아니면(질환 나열 등) 실명 뒤에 그대로 유지.
      const inner = paren ? paren.slice(1, -1) : "";
      const trailing = paren && !isLabelParen(inner) ? paren : "";

      const body =
        render === "plain"
          ? entry.name
          : `[${escapeForLinkText(entry.name)}](#hospital-${alias})`;
      return `${prefix}${body}${trailing}${suffix}`;
    }
  );
}
